import React, { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { StyledSelectInput } from "@aureskonnect/react-ui";
import classnames from "classnames";
import { useSnapshot } from "valtio";

import {
    setPendingBarcodeChecked,
    setPendingBarcodePosition,
    setPendingQrCodeChecked,
    setPendingQrCodePosition,
    setOrderBarcodeChecked,
    setOrderBarcodePosition,
    setOrderQrCodeChecked,
    setOrderQrCodePosition,
    storeDivers,
} from "@components/Common/PrinterSetting/stores";

import ConfigQRCode from "./ConfigQRCode";
import PaperlessTicket from "./PaperlessTicket";
import "../index.css";
import { positionOptions } from "@constants/index";

function CodeRow({
    barcodeChecked,
    barcodePosition,
    qrCodeChecked,
    qrCodePosition,
    onBarcodeCheckedChange,
    onBarcodePositionChange,
    onQrCodeCheckedChange,
    onQrCodePositionChange,
    type,
}: {
    barcodeChecked: boolean;
    barcodePosition: string;
    qrCodeChecked: boolean;
    qrCodePosition: string;
    onBarcodeCheckedChange: (val: boolean) => void;
    onBarcodePositionChange: (val: string) => void;
    onQrCodeCheckedChange: (val: boolean) => void;
    onQrCodePositionChange: (val: string) => void;
    type: "orderPending" | "order";
}) {
    const { t } = useTranslation();

    const selectStyles = {
        valueContainer: () => ({
            display: "flex",
            alignItems: "center",
        }),
        menu: (base: CSSProperties) => ({
            ...base,
            zIndex: 50,
            backgroundColor: "#fff",
            width: "220px",
        }),
    };

    return (
        <div className="d-flex align-items-center flex-wrap py-2 px-2">
            <div className="d-flex align-items-center code-row-group__clz">
                <input
                    id={`barcode-${type}`}
                    className={`${classnames(
                        {
                            afa_chk_barcodeOnPendingTicket:
                                type === "orderPending",
                        },
                        { afa_chk_barcodeOnTicket: type === "order" }
                    )}`}
                    type="checkbox"
                    checked={barcodeChecked}
                    onChange={() => {
                        onBarcodeCheckedChange(!barcodeChecked);
                        if (!barcodeChecked && barcodePosition === "") {
                            onBarcodePositionChange(
                                positionOptions(t)[0].value
                            );
                        }
                    }}
                />
                <label
                    htmlFor={`barcode-${type}`}
                    className="mx-3 mb-0 form-check-label"
                >
                    {t("Barcode on ticket")}
                </label>
                <StyledSelectInput
                    styles={selectStyles}
                    value={{
                        label:
                            barcodePosition === positionOptions(t)[1].value
                                ? t("At the top of the ticket")
                                : t("In the bottom of the ticket"),
                        value: barcodePosition || positionOptions(t)[0].value,
                    }}
                    onChange={(e: any) => {
                        onBarcodePositionChange(e.value);
                    }}
                    options={positionOptions(t)}
                    className={`${classnames(
                        "input-styled__clz ",
                        {
                            "not-allowed-icon__clz": !barcodeChecked,
                        },
                        {
                            afa_drp_selectDrpBarcodeOnPendingTicket:
                                type === "orderPending",
                        },
                        { afa_drp_selectDrpBarcodeOnTicket: type === "order" }
                    )}`}
                />
            </div>

            <div className="d-flex align-items-center ml-5 code-row-group__clz">
                <input
                    id={`qr-${type}`}
                    type="checkbox"
                    className={`${classnames(
                        {
                            afa_chk_qrCodePendingTicket:
                                type === "orderPending",
                        },
                        { afa_chk_qrCode: type === "order" }
                    )}`}
                    checked={qrCodeChecked}
                    onChange={() => {
                        onQrCodeCheckedChange(!qrCodeChecked);
                        if (!qrCodeChecked && qrCodePosition === "") {
                            onQrCodePositionChange(positionOptions(t)[0].value);
                        }
                    }}
                />
                <label
                    htmlFor={`qr-${type}`}
                    className="mx-3 mb-0 form-check-label"
                >
                    {t("QR Code")}
                </label>
                <StyledSelectInput
                    styles={selectStyles}
                    value={{
                        label:
                            qrCodePosition === positionOptions(t)[1].value
                                ? t("At the top of the ticket")
                                : t("In the bottom of the ticket"),
                        value: qrCodePosition || positionOptions(t)[0].value,
                    }}
                    onChange={(e: any) => {
                        onQrCodePositionChange(e.value);
                    }}
                    options={positionOptions(t)}
                    className={`${classnames(
                        "input-styled__clz ",
                        {
                            "not-allowed-icon__clz": !qrCodeChecked,
                        },
                        {
                            afa_drp_selectDrpQrCodePendingTicket:
                                type === "orderPending",
                        },
                        { afa_drp_selectDrpQrCode: type === "order" }
                    )}`}
                />
            </div>
        </div>
    );
}

export default function Code() {
    const { t } = useTranslation();
    const {
        pendingBarcodeChecked,
        pendingBarcodePosition,
        pendingQrCodeChecked,
        pendingQrCodePosition,
        orderBarcodeChecked,
        orderBarcodePosition,
        orderQrCodeChecked,
        orderQrCodePosition,
    } = useSnapshot(storeDivers);

    return (
        <div>
            <div className="p-2">
                <b>{t("Pending order")}</b>
            </div>
            <CodeRow
                barcodeChecked={pendingBarcodeChecked}
                barcodePosition={pendingBarcodePosition}
                qrCodeChecked={pendingQrCodeChecked}
                qrCodePosition={pendingQrCodePosition}
                onBarcodeCheckedChange={setPendingBarcodeChecked}
                onBarcodePositionChange={setPendingBarcodePosition}
                onQrCodeCheckedChange={setPendingQrCodeChecked}
                onQrCodePositionChange={setPendingQrCodePosition}
                type={"orderPending"}
            />

            <div className="border-footer__clz p-1" />

            <div className="p-2 mt-2">
                <b>{t("Order")}</b>
            </div>
            <CodeRow
                barcodeChecked={orderBarcodeChecked}
                barcodePosition={orderBarcodePosition}
                qrCodeChecked={orderQrCodeChecked}
                qrCodePosition={orderQrCodePosition}
                onBarcodeCheckedChange={setOrderBarcodeChecked}
                onBarcodePositionChange={setOrderBarcodePosition}
                onQrCodeCheckedChange={setOrderQrCodeChecked}
                onQrCodePositionChange={setOrderQrCodePosition}
                type={"order"}
            />
            <ConfigQRCode />
            <div className="border-footer__clz p-1" />
            <PaperlessTicket />
        </div>
    );
}
